import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import i18n, { applyDocumentLocale } from './config';
import { DEFAULT_LOCALE, isSupportedLocale } from './languages';
import { patchSettingsBlob } from '@/lib/settingsStore';
import { useProfile } from '@/api/hooks/useWeight';

interface LocaleContextValue {
  locale: string;
  setLocale: (code: string) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => i18n.language || DEFAULT_LOCALE);
  const profileQ = useProfile();

  const setLocale = useCallback((code: string) => {
    const next = isSupportedLocale(code) ? code : DEFAULT_LOCALE;
    setLocaleState(next);
    void i18n.changeLanguage(next);
    applyDocumentLocale(next);
    patchSettingsBlob({ locale: next });
  }, []);

  // The profile is the cross-device source of truth: when it loads (or changes
  // after sign-in on a new device), adopt its locale if it differs from what we
  // booted with from localStorage.
  const profileLocale = profileQ.data?.locale;
  useEffect(() => {
    if (isSupportedLocale(profileLocale) && profileLocale !== locale) {
      setLocale(profileLocale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLocale]);

  return <LocaleContext.Provider value={{ locale, setLocale }}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within LocaleProvider');
  return ctx;
}
